import React from 'react';

import { Flex, Popconfirm, Space, Tooltip } from 'antd-v5';
import { MdAssignment, MdEdit } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Button, Text } from 'scouting-ui/lib';

import type { CounselorMeritBadge, MBCRecord } from '@appTypes/esb';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { SearchFormState } from '@modules/meritBadgeCounselors/types';
import { getPreferredName } from '@modules/shared/utils/preferredName';
import { PersonAvatar } from '@shared/components';
import { toastService } from '@toasts';

import {
  AvailabilityFilterOptions,
  CounselorAvailabilityShort,
  NON_US_VALUE,
  SearchTableActions,
  TableMode,
} from '../../constants';
import { numberWithDecimal } from '../../duck/utils';

interface MBCTableRowsProps {
  hasCouncilLevelPosition?: boolean;
  hasDistrictLevelPosition?: boolean;
  hasEditAssignPerm: boolean;
  isCouncilOrg: boolean;
  mode?: TableMode;
  onAction?: (key: SearchTableActions, counselor: MBCRecord) => void;
  onEdit?: (memberId: number) => () => void;
  searchValue: SearchFormState;
  showDistance: boolean;
}

const rows =
  ({
    hasCouncilLevelPosition,
    hasDistrictLevelPosition,
    hasEditAssignPerm,
    isCouncilOrg,
    mode,
    onAction,
    onEdit,
    searchValue,
    showDistance: searchWithZip,
  }: MBCTableRowsProps) =>
  (item: MBCRecord) => {
    const searchType = searchValue.orgGuidType ?? '';
    const personFullName = getPreferredName(item);
    const citySate = `${item.city}, ${item.state}`;
    const isAvailable =
      CounselorAvailabilityShort.AVAILABLE === item.counselorAvailabilityShort;
    const availableContent = isAvailable ? (
      <Text>{AvailabilityFilterOptions.AVAILABLE}</Text>
    ) : (
      <Text>{AvailabilityFilterOptions.BUSY}</Text>
    );

    const listOfMBs = item.meritBadges
      .map((mb: CounselorMeritBadge) => mb.meritBadgeName)
      .join(', ');
    const listOfMBsContent = (
      <Text
        style={{
          whiteSpace: 'normal',
          wordBreak: 'keep-all',
        }}
      >
        {listOfMBs}
      </Text>
    );

    const mbCount = item.meritBadges.length || 0;
    const mbCountContent = (
      <Text
        style={{
          whiteSpace: 'normal',
          wordBreak: 'keep-all',
        }}
      >
        {mbCount}
      </Text>
    );

    const items = [
      {
        rowId: 'profilePicture',
        content: (
          <PersonAvatar
            isAdult={true}
            gender={item.gender}
            pictureUrl={item.profilePicture}
          />
        ),
      },
      {
        rowId: 'Name',
        label: (
          <Text>
            <FormattedMessage id="page.mbcSearch.mobileTable.label.name" />
          </Text>
        ),
        content: <Text>{personFullName}</Text>,
      },
      ...(featureFlags.getFlag('SBL_6772_SHOW_MEMBER_ID_COLUMN')
        ? [
            {
              rowId: 'memberId',
              label: (
                <Text>
                  <FormattedMessage id="page.mbcSearch.mobileTable.label.mid" />
                </Text>
              ),
              content: <Text>{item.memberId}</Text>,
            },
          ]
        : []),
      {
        rowId: 'city',
        label: (
          <Text>
            <FormattedMessage id="page.mbcSearch.mobileTable.label.city" />
          </Text>
        ),
        content: <Text>{citySate}</Text>,
      },
      ...(searchWithZip
        ? [
            {
              rowId: 'zipCode',
              label: (
                <Text>
                  <FormattedMessage id="page.mbcSearch.mobileTable.label.zipCode" />
                </Text>
              ),
              content: <Text>{item.zipCode}</Text>,
            },
            {
              rowId: 'distance',
              label: (
                <Text>
                  <FormattedMessage id="page.mbcSearch.mobileTable.label.distance" />
                </Text>
              ),
              content: <Text>{numberWithDecimal(item.distance, 2)}</Text>,
            },
          ]
        : []),
      {
        rowId: 'scoutCount',
        label: (
          <Text>
            <FormattedMessage id="page.mbcSearch.mobileTable.label.counseling" />
          </Text>
        ),
        content: <Text>{item.scoutCount}</Text>,
      },
      {
        rowId: 'counselorAvailabilityShort',
        label: (
          <Text>
            <FormattedMessage id="page.mbcSearch.mobileTable.label.availability" />
          </Text>
        ),
        content: availableContent,
      },
      {
        rowId: 'meritBadges',
        label: (
          <Text>
            <FormattedMessage id="page.mbcSearch.mobileTable.label.meritBadges" />
          </Text>
        ),
        content: listOfMBsContent,
      },
      {
        rowId: 'meritBadges',
        label: (
          <Text>
            <FormattedMessage id="page.mbcSearch.mobileTable.label.count" />
          </Text>
        ),
        content: mbCountContent,
      },
      mode === TableMode.ASSIGN_MBCS
        ? {
            rowId: 'meritBadges',
            content: (
              <Space align="center">
                <Tooltip title="Remove">
                  <Popconfirm
                    title={
                      <FormattedMessage id="mbcDashboard.mbTable.assignMbcConfirm.title" />
                    }
                    description={
                      <FormattedMessage
                        values={{
                          mbcName: `${item.firstName} ${item.lastName}`,
                        }}
                        id="mbcDashboard.mbTable.assignMbcConfirm"
                      />
                    }
                    onConfirm={() => {
                      onAction?.(SearchTableActions.ASSIGN, item);
                    }}
                    okButtonProps={{ danger: true }}
                    cancelButtonProps={{
                      type: 'default',
                    }}
                    onCancel={() => null}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button shape="round" type="text" size="small">
                      <FormattedMessage id="youthProfile.AdvMbc.modal.assign" />
                    </Button>
                  </Popconfirm>
                </Tooltip>
              </Space>
            ),
          }
        : {
            rowId: 'meritBadges',
            content: (
              <Flex flex={1} justify="flex-end">
                {(hasDistrictLevelPosition || hasCouncilLevelPosition) &&
                  searchType === NON_US_VALUE && (
                    <Button
                      type="default"
                      icon={<MdEdit />}
                      onClick={() =>
                        toastService.warn(
                          <FormattedMessage id="mbcDashboard.mbTable.column.edit.warningMessage" />,
                        )
                      }
                    >
                      <FormattedMessage id="shared.edit" />
                    </Button>
                  )}

                {(hasEditAssignPerm || isCouncilOrg) && (
                  <Button
                    onClick={onEdit?.(item.memberId)}
                    icon={<MdEdit />}
                    type="default"
                  >
                    <FormattedMessage id="shared.edit" />
                  </Button>
                )}

                {!hasEditAssignPerm &&
                  !(
                    (hasDistrictLevelPosition || hasCouncilLevelPosition) &&
                    searchType === NON_US_VALUE
                  ) &&
                  !isCouncilOrg && (
                    <Button
                      onClick={() =>
                        onAction?.(SearchTableActions.ASSIGN, item)
                      }
                      icon={<MdAssignment />}
                      type="default"
                    >
                      <FormattedMessage id="mbcDashboard.mbTable.column.assign" />
                    </Button>
                  )}
              </Flex>
            ),
          },
    ];

    return items;
  };

export default rows;
